import { useEffect, useState } from "react"
import { FiBookOpen, FiCheckSquare, FiFileText, FiCreditCard, FiRefreshCw, FiSave, FiHash } from "react-icons/fi"
import AppLayout from "../layout/AppLayout"
import FileUploadSlot from "../components/patent/FileUploadSlot"
import SectionHeader from "../components/ui/SectionHeader"
import ProgressBarV2 from "../components/ui/ProgressBar_v2"
import FeesBoxV2 from "../components/trademark/sidebar/FeesBox_v2"
import CurrencySelector from "../components/layout/CurrencySelector"
import { FilingSidebarAccordion } from "../components/ui/FilingSidebarAccordion"

const requests = [
  "Request for grant of a patent based on the claims as filed",
  "Auxiliary request for oral proceedings"
]

const argumentSections = [
  { id: "article-123", title: "Impermissible extension / support of amended claims - Art. 123 EPC" },
  { id: "article-84", title: "Clarity - Art. 84 EPC" },
  { id: "article-54", title: "Novelty - Art. 54 EPC" },
  { id: "article-56", title: "Obviousness - Art. 56 EPC" }
] as const

const documents = [
  { id: "claims-markup", label: "Amended claims (marked-up version)" },
  { id: "claims-clean", label: "Amended claims (clean version)" },
  { id: "description-markup", label: "Amended description (marked-up version)" },
  { id: "description-clean", label: "Amended description (clean version)" }
] as const
const progressSections = [
  { target: "application-number", label: "Application number", short: "Application number", icon: <FiHash /> },
  { target: "requests", label: "Requests", short: "Requests", icon: <FiCheckSquare /> },
  { target: "arguments", label: "Arguments", short: "Arguments", icon: <FiBookOpen /> },
  { target: "documents", label: "Amended documents", short: "Documents", icon: <FiFileText /> }
]
const secondaryButtonClass = "flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-dantzBlue transition-colors hover:border-dantzBlue hover:bg-blue-50"

type ArgumentSectionId = (typeof argumentSections)[number]["id"]
type DocumentId = (typeof documents)[number]["id"]

export default function OfficeActionPage() {
  const [applicationNumber, setApplicationNumber] = useState("")
  const [selectedRequests, setSelectedRequests] = useState<boolean[]>([true, true])
  const [activeArguments, setActiveArguments] = useState<Partial<Record<ArgumentSectionId, boolean>>>({})
  const [argumentsText, setArgumentsText] = useState<Partial<Record<ArgumentSectionId, string>>>({})
  const [files, setFiles] = useState<Partial<Record<DocumentId, File>>>({})
  const [activeSection, setActiveSection] = useState("application-number")
  const [statusMessage, setStatusMessage] = useState("")

  const complete = {
    "application-number": Boolean(applicationNumber.trim()),
    requests: selectedRequests.some(Boolean),
    arguments: argumentSections.some(({ id }) => activeArguments[id] && argumentsText[id]?.trim()),
    documents: documents.every(({ id }) => Boolean(files[id]))
  }

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
      if (visible.length) setActiveSection(visible[0].target.id)
    }, { rootMargin: "-10% 0px -65% 0px", threshold: 0 })
    progressSections.forEach(({ target }) => {
      const section = document.getElementById(target)
      if (section) observer.observe(section)
    })
    return () => observer.disconnect()
  }, [])

  function resetForm() {
    setApplicationNumber("")
    setSelectedRequests([true, true])
    setActiveArguments({})
    setArgumentsText({})
    setFiles({})
    setActiveSection("application-number")
    setStatusMessage("Form reset.")
  }

  return (
    <AppLayout title="Patent Office Action Response" wide>
      <div className="sticky top-0 z-30 border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-4">
          <nav aria-label="Form sections">
            <ProgressBarV2
              complete={complete}
              sections={progressSections}
              activeSection={activeSection}
              onNavigate={setActiveSection}
            />
          </nav>
        </div>
      </div>
      <div className="flex justify-center py-12 pb-28">
        <div className="grid w-full max-w-7xl grid-cols-1 items-start gap-7 xl:grid-cols-[minmax(0,1fr)_18rem]">
          <form className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <section id="application-number" className="scroll-mt-32 border-b border-gray-200 p-5 sm:p-8 xl:p-12">
            <SectionHeader
              icon={<FiHash />}
              title="Application number"
              subtitle="Enter the European patent application number for this response."
            />
            <label className="block text-sm font-medium text-gray-700">
              European patent application number *
              <input
                type="text"
                required
                value={applicationNumber}
                onChange={event => setApplicationNumber(event.target.value)}
                className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-dantzBlue focus:outline-none focus:ring-1 focus:ring-dantzBlue"
              />
            </label>
          </section>
          <section id="requests" className="scroll-mt-32 border-b border-gray-200 p-5 sm:p-8 xl:p-12">
            <SectionHeader
              icon={<FiCheckSquare />}
              title="Requests"
              subtitle="Select the requests to be included in your response."
            />
            <fieldset className="space-y-3">
              <legend className="sr-only">Requests</legend>
              {requests.map((request, index) => (
                <label key={request} className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-4 text-sm text-gray-800 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 accent-dantzBlue"
                    checked={selectedRequests[index]}
                    onChange={event => setSelectedRequests(current => current.map((selected, itemIndex) => itemIndex === index ? event.target.checked : selected))}
                  />
                  <span>{request}</span>
                </label>
              ))}
            </fieldset>
          </section>

          <section id="arguments" className="scroll-mt-32 border-b border-gray-200 p-5 sm:p-8 xl:p-12">
            <SectionHeader
              icon={<FiBookOpen />}
              title="Arguments"
              subtitle="Select each ground you want to address and add your prepared argument."
            />
            <div className="space-y-4">
              {argumentSections.map(({ id, title }) => {
                const active = Boolean(activeArguments[id])
                return (
                  <fieldset key={id} aria-label={title} className="rounded-lg border border-gray-200 p-4">
                    <label className="flex cursor-pointer items-start gap-3 text-sm font-medium text-gray-800">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 shrink-0 accent-dantzBlue"
                        checked={active}
                        onChange={event => setActiveArguments(current => ({ ...current, [id]: event.target.checked }))}
                      />
                      <span>{title}</span>
                    </label>
                    {active && (
                      <label className="mt-4 block text-sm font-medium text-gray-700">
                        {title} argument
                        <textarea
                          aria-label={`${title} argument`}
                          rows={6}
                          value={argumentsText[id] ?? ""}
                          onChange={event => setArgumentsText(current => ({ ...current, [id]: event.target.value }))}
                          className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-dantzBlue focus:outline-none focus:ring-1 focus:ring-dantzBlue"
                        />
                      </label>
                    )}
                  </fieldset>
                )
              })}
            </div>
          </section>

          <section id="documents" className="scroll-mt-32 p-5 sm:p-8 xl:p-12">
            <SectionHeader
              icon={<FiFileText />}
              title="Amended documents"
              subtitle="Upload both the marked-up version and the clean version of amended claims and description."
            />
            <div className="grid gap-5 sm:grid-cols-2">
              {documents.map(({ id, label }) => (
                <FileUploadSlot
                  key={id}
                  label={label}
                  file={files[id]}
                  fileName={files[id]?.name}
                  uploading={false}
                  onSelect={file => setFiles(current => ({ ...current, [id]: file }))}
                  onDelete={() => setFiles(current => ({ ...current, [id]: undefined }))}
                />
              ))}
            </div>
          </section>
          </form>

          <aside className="block">
            <div className="flex w-full flex-col gap-4 xl:sticky xl:top-[128px] xl:max-h-[calc(100vh-9rem)] xl:w-72 xl:overflow-y-auto xl:[scrollbar-width:none] xl:[&::-webkit-scrollbar]:hidden">
              <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-3 text-sm shadow-sm">
                <div className="mb-1 flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Status</span>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Draft</span>
                </div>
                <button type="button" onClick={() => setStatusMessage("Saving drafts is not available in this demo.")} className={secondaryButtonClass}>
                  <FiSave className="h-4 w-4" aria-hidden="true" /> Save as draft
                </button>
                <button type="button" onClick={resetForm} className={secondaryButtonClass}>
                  <FiRefreshCw className="h-4 w-4" aria-hidden="true" /> Reset form
                </button>
                <button type="button" onClick={() => setStatusMessage("Payment is not available in this demo.")} className="flex w-full items-center justify-center gap-2 rounded-lg bg-dantzOrange px-3 py-2 text-sm font-medium text-white transition-colors hover:brightness-110">
                  <FiCreditCard className="h-4 w-4" aria-hidden="true" /> Proceed to payment
                </button>
                {statusMessage && <p role="status" className="text-xs text-dantzBlue">{statusMessage}</p>}
              </div>
              <FilingSidebarAccordion>
                <FeesBoxV2
                  total={160}
                  serviceLines={[{ label: "Office action response service", amount: 160 }]}
                  officialLines={[]}
                />
                <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
                  <CurrencySelector />
                </div>
              </FilingSidebarAccordion>
            </div>
          </aside>
        </div>
      </div>
    </AppLayout>
  )
}
